import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../../utils/AuthContext';
import io from 'socket.io-client';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { CgAttachment } from "react-icons/cg";
import { IoIosCloseCircle } from "react-icons/io";
import { hostNameBack } from '../../utils/constants';
import { FaFileAlt, FaFileExcel, FaFilePdf, FaFilePowerpoint, FaFileWord } from 'react-icons/fa';
import Loader from '../../utils/Loader';
import { Link } from 'react-router-dom';

let socket;

const ChatRoom = ({ participant, sellerParticipant, isParticipantAdmin, isPopup, onClose }) => {
    const { user, admin } = useContext(AuthContext);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [file, setFile] = useState(null);
    const [sendLoading, setSendLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const token = localStorage.getItem('token');
    const adminToken = localStorage.getItem('adminToken');
    const [fullLoading, setFullLoading] = useState(false);

    // Fetch existing messages when the participant changes
    useEffect(() => {
        if (!user && !admin) return;

        axios.get(`${hostNameBack}/api/v1/chats/${user ? "userChats" : "adminChats"}/${user ? user._id : admin._id}`, {
            headers: { Authorization: `Bearer ${user ? token : adminToken}` }
        })
            .then(response => {
                if (response.data.success) {
                    const chats = response.data.chats;
                    const chat = chats.find(c =>
                        (c.participantId?._id === participant._id && !isParticipantAdmin) ||
                        (c.adminParticipantId?._id === participant._id && isParticipantAdmin)
                    );
                    if (chat) {
                        setMessages(chat.messages);
                    } else {
                        setMessages([]);
                    }
                }
            })
            .catch(e => {
                enqueueSnackbar("Failed to fetch messages!", { variant: "error" });
            });
    }, [participant, isParticipantAdmin, user, admin, token, adminToken]);

    // Set up socket listener for real-time messages
    useEffect(() => {
        if (!socket) {
            const userId = user ? user._id : admin ? admin._id : null;
            socket = io(`${hostNameBack}`, { query: { userId } });
        }

        const handleReceiveMessage = (newMessage) => {
            if (newMessage.senderId === participant._id) {
                setMessages(prevMessages => [...prevMessages, newMessage]);
            }
        };

        socket.on('receiveMessage', handleReceiveMessage);

        return () => {
            socket.off('receiveMessage', handleReceiveMessage);
        };
    }, [participant, user, admin]);

    // Scroll to the latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Send message function
    const sendMessage = async () => {
        if (!message.trim() && !file) return;
        if (sendLoading) return;

        setSendLoading(true);

        const formData = new FormData();
        formData.append('senderId', user ? user._id : admin._id);
        formData.append('receiverId', participant._id);
        formData.append('receiverEmail', participant.email);
        formData.append('text', message);
        formData.append('isParticipantAdmin', isParticipantAdmin);
        if (file) formData.append('file', file);

        try {
            const response = await axios.post(
                `${hostNameBack}/api/v1/chats/sendMessage/${user ? "user" : "admin"}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${user ? token : adminToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                setMessages(prevMessages => [...prevMessages, response.data.message]);
                setMessage("");
                setFile(null);
                socket.emit('sendMessage', response.data.message);
            }
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "Failed to send message!", { variant: "error" });
        } finally {
            setSendLoading(false);
        }
    };

    const handleFileChange = (event) => {
        setFile(event.target.files[0]);
    };

    const removeFile = () => {
        if (!sendLoading) setFile(null);
    };

    const formatDateTime = (date) => {
        const options = { month: 'short', day: 'numeric', year: '2-digit' };
        const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
        const formattedDate = new Date(date).toLocaleDateString(undefined, options);
        const formattedTime = new Date(date).toLocaleTimeString(undefined, timeOptions);
        return `${formattedTime} - ${formattedDate}`;
    };

        const handleFileDownload = async (fileUrl, originalFileName) => {
        try {
            setFullLoading(true);
            const backendUrl = hostNameBack || 'http://localhost:5000';

            const response = await axios({
                url: `${backendUrl}/api/v1/chats/download`,
                method: 'GET',
                params: {
                    fileUrl,
                    originalFileName
                },
                responseType: 'blob',
                withCredentials: true
            });

            const contentDisposition = response.headers['content-disposition'];
            const downloadFileName = contentDisposition
                ? contentDisposition.split('filename=')[1].replace(/"/g, '')
                : originalFileName;

            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const downloadLink = document.createElement('a');
            downloadLink.href = blobUrl;
            downloadLink.download = downloadFileName;

            document.body.appendChild(downloadLink);
            downloadLink.click();

            setTimeout(() => {
                document.body.removeChild(downloadLink);
                window.URL.revokeObjectURL(blobUrl);
            }, 100);
        } catch (error) {
        }
        finally {
            setFullLoading(false);
        }
    };

        const renderDocumentIcon = (fileType) => {
            if (fileType === 'application/pdf') return <FaFilePdf size={18} />;
            if (fileType === 'application/msword' || fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return <FaFileWord size={18} />;
            if (fileType === 'application/vnd.ms-excel' || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return <FaFileExcel size={18} />;
            if (fileType === 'application/vnd.ms-powerpoint' || fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return <FaFilePowerpoint size={18} />;
            return <FaFileAlt size={18} />;
        };

    if (!user && !admin) return <div>Please log in to chat.</div>;

    return (
        <div className={`floatChatRoom ${isPopup ? 'popup' : ''}`}>

            {fullLoading && <Loader type="checkmate" />}

            <div className="header">
                <img
                    src={participant?.role === "seller" ? `${sellerParticipant?.profileImage}` : "/assets/images/seller.png"}
                    alt="Profile"
                />
                <span>{participant.username}</span>
                {isPopup && <button onClick={onClose}>Close</button>}
            </div>
            <div className="messages content">
                {messages.length > 0 ? messages.map((msg, index) => (
                    <div
                        key={index}
                        className={msg.senderId === (user ? user._id : admin._id) ? "message sent" : "message received"}
                    >
                        <div className='senderName fw600 lightBlackPrimary fs09'>
                            {msg.senderId !== (user ? user._id : admin._id) && <div>{participant.username}</div>}
                            <span className='messageTime'>{formatDateTime(msg.timestamp)}</span>
                        </div>
                        <div>{msg.text}</div>
                        {msg.fileUrl && (
                            <div className="fileBox">
                                {msg.fileType.startsWith('image/') && (
                                    <img src={`${msg.fileUrl}`} alt="Attachment" style={{ maxWidth: '100px', maxHeight: '100px', cursor: "pointer" }} />
                                )}
                                {msg.fileType.startsWith('video/') && (
                                    <video controls style={{ maxWidth: '100%', maxHeight: '400px' }}>
                                        <source src={`${msg.fileUrl}`} type={msg.fileType} />
                                        Your browser does not support the video tag.
                                    </video>
                                )}
                                {(msg.fileType.startsWith('application/') || msg.fileType.startsWith('text/')) && (
                                    <div className="documentBox" onClick={() => handleFileDownload(msg.fileUrl, msg.originalFileName)}>
                                        {renderDocumentIcon(msg.fileType)}
                                        <p>{msg.originalFileName}</p>
                                    </div>
                                )}
                            </div>)}
                        {msg.offer && (
                            <p className='offerLink'>Open <Link to="/chat">Chats</Link> to view this offer</p>
                        )}
                    </div>
                )) : <div className="noChats">No messages yet</div>}
                <div ref={messagesEndRef} />
            </div>
            <div className="writeMessageDiv">
                {file && (
                    <div className="filePreview">
                        <p>{file.name}</p>
                        <IoIosCloseCircle className='removeFileIcon' onClick={removeFile} />
                    </div>
                )}
                <div className="writeMessageMain">
                    <label htmlFor='fileInput'><CgAttachment className='attachIcon icon' /></label>
                    <input
                        type="file"
                        className="hidden"
                        id="fileInput"
                        onChange={handleFileChange}
                        disabled={sendLoading}
                        style={{ display: 'none' }}
                    />
                    <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder='Type a Message'
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                    />
                    <button
                        className='primaryBtn'
                        onClick={sendMessage}
                        disabled={sendLoading}
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatRoom;